export enum Stage {
  PROD = 'prod',
  GAMMA = 'gamma',
  BETA = 'beta',
  LOCAL = 'local',
}

export interface RumConfig {
  appId: string;
  identityPoolId: string;
  guestRoleArn: string;
  region: string;
}

/**
 * Available feature flags in the system.
 */
export type FeatureId = 'UxRedesign' | 'EarlySharing';

export interface EnvironmentConfig {
  mplServiceEndpoint: string;
  // TODO: remove mplServiceAPIGatewayEndpoint once image url has been replaced with custom domain
  mplServiceAPIGatewayEndpoint?: string;
  portalEndpoint: string;
  tokenIssuer: string;
  rumConfig?: RumConfig;
  entitiesEndpoint?: string;
  featureFlags?: FeatureId[];
}

function getEnvironment(): Stage {
  const hostname = window && window.location && window.location.hostname;

  if (
    hostname === 'mpl.beta.caracal.proserve.aws.a2z.com' ||
    hostname === 'beta.apg-library.proserve.aws.dev'
  ) {
    return Stage.BETA;
  }

  if (
    hostname === 'mpl.gamma.caracal.proserve.aws.a2z.com' ||
    hostname === 'gamma.apg-library.proserve.aws.dev'
  ) {
    return Stage.GAMMA;
  }

  if (hostname === 'apg-library.amazonaws.com' || hostname === 'cdk.apg-library.amazonaws.com') {
    return Stage.PROD;
  }

  return Stage.LOCAL;
}

// TODO
// After deploying Cloudfront and S3 bucket for entities in beta, gamma and prod environments,
// update these URLs.
function getConfig(): EnvironmentConfig {
  const environment = getEnvironment();

  if (environment === Stage.BETA) {
    return {
      mplServiceEndpoint: 'https://api1.beta.apg-library.proserve.aws.dev/v1',
      mplServiceAPIGatewayEndpoint: 'https://4ej9okcm28.execute-api.us-east-1.amazonaws.com/v1',
      portalEndpoint: 'https://portal.beta.apg-library.proserve.aws.dev',
      tokenIssuer: 'https://cognito-idp.us-west-2.amazonaws.com/us-west-2_x03PU1vmu',
      rumConfig: {
        appId: 'c15b4400-3c91-4e93-852c-f6af7f50d2c2',
        identityPoolId: 'us-east-1:d856badb-986b-4d1d-b25e-6d5f18c8e44d',
        guestRoleArn:
          'arn:aws:iam::268364832669:role/RUM-Monitor-us-east-1-268364832669-APGL-Unauth',
        region: 'us-east-1',
      },
      entitiesEndpoint: 'https://d2bj6x7f6ri1ir.cloudfront.net/aws-docs-entities.json',
      featureFlags: ['EarlySharing', 'UxRedesign'],
    };
  }

  if (environment === Stage.GAMMA) {
    return {
      mplServiceEndpoint: 'https://api1.gamma.apg-library.proserve.aws.dev/v1',
      mplServiceAPIGatewayEndpoint: 'https://ickv1lyk42.execute-api.us-east-1.amazonaws.com/v1',
      portalEndpoint: 'https://portal.gamma.apg-library.proserve.aws.dev',
      tokenIssuer: 'https://cognito-idp.us-west-2.amazonaws.com/us-west-2_tUnnJ4uzf',
      rumConfig: {
        appId: 'aef507c9-f7d9-4d93-a9af-cce28d09f81f',
        identityPoolId: 'us-east-1:92779df1-32b8-4235-a344-edcb928eb936',
        guestRoleArn:
          'arn:aws:iam::208280417849:role/RUM-Monitor-us-east-1-208280417849-APGL-Unauth',
        region: 'us-east-1',
      },
      entitiesEndpoint: 'https://d1qegrhm9nijft.cloudfront.net/aws-docs-entities.json',
      featureFlags: ['EarlySharing', 'UxRedesign'],
    };
  }

  if (environment === Stage.PROD) {
    return {
      mplServiceEndpoint: 'https://api1.prod.apg-library.proserve.aws.dev/v1',
      mplServiceAPIGatewayEndpoint: 'https://vzm39wj0ik.execute-api.us-east-1.amazonaws.com/v1',
      portalEndpoint: 'https://accelerate.amazonaws.com',
      tokenIssuer: 'https://cognito-idp.us-west-2.amazonaws.com/us-west-2_T18JUTBSR',
      rumConfig: {
        appId: '0a6bc6ec-58a4-468e-ac4e-52ac27b6ca2d',
        identityPoolId: 'us-east-1:c528adbd-fd70-4cc0-8529-f9036c3a66eb',
        guestRoleArn:
          'arn:aws:iam::370440150149:role/RUM-Monitor-us-east-1-370440150149-APGL-Unauth',
        region: 'us-east-1',
      },
      entitiesEndpoint: 'https://d3b7y15i2nhg79.cloudfront.net/aws-docs-entities.json',
      featureFlags: ['EarlySharing', 'UxRedesign'],
    };
  }

  return {
    // @ts-ignore
    mplServiceEndpoint: MPC_SERVICE_ENDPOINT,
    // @ts-ignore
    mplServiceAPIGatewayEndpoint: MPC_SERVICE_API_GATEWAY_ENDPOINT,
    // @ts-ignore
    portalEndpoint: PORTAL_ENDPOINT,
    // @ts-ignore
    tokenIssuer: PORTAL_TOKEN_ISSUER,
    // @ts-ignore
    rumConfig: RUM_CONFIG,
    // @ts-ignore
    entitiesEndpoint: ENTITIES_ENDPOINT,
    // @ts-ignore
    featureFlags: FEATURE_FLAGS ?? [],
  };
}

export default {
  getConfig,
  getEnvironment,
};

export function isFeatureEnabled(featureId: FeatureId): boolean {
  return getConfig().featureFlags?.includes(featureId) ?? false;
}
